import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Edit3, Loader2, Palette, Ruler, Sparkles, Search, X, Check } from "lucide-react";
import { toast } from "sonner";
import { matchesAll } from "@/lib/trFold";

const TYPE_META = {
  size: { label: "Beden", Icon: Ruler, cls: "border-blue-700 bg-blue-950/40 text-blue-300" },
  color: { label: "Renk", Icon: Palette, cls: "border-emerald-700 bg-emerald-950/40 text-emerald-300" },
  other: { label: "Diğer", Icon: Sparkles, cls: "border-slate-700 bg-slate-950 text-slate-300" },
};

const emptyForm = { name: "", variant_type: "size", options_text: "", description: "" };

export default function VariantSets() {
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/variant-sets");
      setSets(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sets.filter((s) => {
      if (typeFilter && s.variant_type !== typeFilter) return false;
      if (term) {
        if (!matchesAll(term, [s.name, ...(s.options || [])])) return false;
      }
      return true;
    });
  }, [sets, search, typeFilter]);

  const openNew = () => { setForm(emptyForm); setEditing(null); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      variant_type: s.variant_type,
      options_text: (s.options || []).join(", "),
      description: s.description || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Set adı zorunlu"); return; }
    const options = form.options_text.split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
    if (options.length === 0) { toast.error("En az bir seçenek girin (virgülle veya satır ayırın)"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        variant_type: form.variant_type,
        options,
        description: form.description || "",
      };
      if (editing) {
        await api.patch(`/variant-sets/${editing.id}`, payload);
        toast.success("Set güncellendi");
      } else {
        await api.post("/variant-sets", payload);
        toast.success("Set oluşturuldu");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`"${s.name}" seti silinsin mi? Bu değişiklik geri alınamaz.`)) return;
    try {
      await api.delete(`/variant-sets/${s.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/variant-sets/${s.id}`, { is_active: !s.is_active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Varyant Setleri"
        subtitle="Renk ve beden varyantlarını yönet — reçete ve siparişlerde bu setler kullanılır"
        breadcrumbs={["Ürünler", "Varyantlar"]}
        actions={
          <Button data-testid="add-variant-set-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Yeni Set
          </Button>
        }
      />
      <div className="p-6 lg:p-8 space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
            <Input
              data-testid="variant-search"
              placeholder="Set adı veya seçenekle ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 text-xs h-9"
            />
          </div>
          <Select value={typeFilter || "__all"} onValueChange={(v) => setTypeFilter(v === "__all" ? "" : v)}>
            <SelectTrigger data-testid="variant-type-filter" className="w-[160px] bg-slate-950 border-slate-700 rounded-sm h-9 text-xs">
              <SelectValue placeholder="Tüm tipler" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
              <SelectItem value="__all">Tüm tipler</SelectItem>
              <SelectItem value="size">Beden</SelectItem>
              <SelectItem value="color">Renk</SelectItem>
              <SelectItem value="other">Diğer</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-[10px] font-mono uppercase text-slate-500 ml-auto">
            {filtered.length} / {sets.length}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800 rounded-sm p-10 text-center space-y-3">
            <Sparkles className="w-10 h-10 mx-auto text-slate-700" />
            <div className="text-slate-500 text-sm">{sets.length === 0 ? "Henüz set yok. İlk setinizi oluşturun." : "Filtreye uyan set yok."}</div>
            {sets.length === 0 && (
              <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-500 rounded-sm">
                <Plus className="w-4 h-4 mr-2" /> İlk Seti Oluştur
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((s) => {
              const meta = TYPE_META[s.variant_type] || TYPE_META.other;
              const M = meta.Icon;
              return (
                <Card
                  key={s.id}
                  data-testid={`variant-set-${s.id}`}
                  className={`bg-slate-900 border-slate-800 rounded-sm p-4 space-y-3 ${!s.is_active ? "opacity-60" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-sm border ${meta.cls}`}>
                          <M className="w-3 h-3" />
                          {meta.label}
                        </span>
                        {!s.is_active && (
                          <span className="text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-sm border border-slate-700 text-slate-500">
                            Pasif
                          </span>
                        )}
                      </div>
                      <div className="text-slate-100 font-semibold text-sm truncate">{s.name}</div>
                      {s.description && (
                        <div className="text-[11px] text-slate-500 mt-0.5 truncate">{s.description}</div>
                      )}
                    </div>
                    <div className="flex gap-0.5 shrink-0">
                      <button onClick={() => toggleActive(s)} title={s.is_active ? "Pasif yap" : "Aktif yap"} className="text-slate-400 hover:text-blue-400 p-1">
                        {s.is_active ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      </button>
                      <button data-testid={`edit-variant-${s.id}`} onClick={() => openEdit(s)} className="text-slate-400 hover:text-blue-400 p-1"><Edit3 className="w-3.5 h-3.5" /></button>
                      <button data-testid={`delete-variant-${s.id}`} onClick={() => remove(s)} className="text-slate-400 hover:text-rose-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(s.options || []).map((o, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center text-[11px] px-2 py-0.5 border border-slate-700 bg-slate-950 rounded-sm text-slate-200 font-mono"
                      >
                        {o}
                      </span>
                    ))}
                    {(!s.options || s.options.length === 0) && (
                      <span className="text-[11px] text-slate-500 italic">Seçenek yok</span>
                    )}
                  </div>
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
                    {(s.options || []).length} seçenek
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-500" />
              {editing ? "Varyant Setini Düzenle" : "Yeni Varyant Seti"}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Kendi setinizi oluşturun. Seçenekleri virgülle veya satır ayırarak girin.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Set Adı *</Label>
                <Input
                  data-testid="variant-set-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Örn: Yetişkin Bedenleri / Reflektif Renkler"
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Tip</Label>
                <Select value={form.variant_type} onValueChange={(v) => setForm({ ...form, variant_type: v })}>
                  <SelectTrigger data-testid="variant-set-type" className="bg-slate-950 border-slate-700 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="size">Beden</SelectItem>
                    <SelectItem value="color">Renk</SelectItem>
                    <SelectItem value="other">Diğer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Açıklama</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="opsiyonel"
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
                  Seçenekler * <span className="text-[10px] text-slate-500 normal-case">— virgülle veya satır ayırın</span>
                </Label>
                <Textarea
                  data-testid="variant-set-options"
                  rows={4}
                  required
                  value={form.options_text}
                  onChange={(e) => setForm({ ...form, options_text: e.target.value })}
                  placeholder={form.variant_type === "color" ? "Lacivert, Siyah, Beyaz, Bordo" : "S, M, L, XL, XXL"}
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono resize-none"
                />
                {form.options_text.trim() && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {form.options_text.split(/[,\n]/).map((x) => x.trim()).filter(Boolean).map((v, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 border border-blue-900/60 bg-blue-950/30 text-blue-200 rounded-sm font-mono">
                        {v}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">İptal</Button>
              <Button data-testid="save-variant-set" type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
